import React from "react";
import {
  AttributeEditor,
  AttributeEditorProps,
  Button,
  Container,
  FormField,
  Header,
  Select,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsFilterOperator,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import {
  OmsFilterCondition,
  OmsFilterCriteriaState,
} from "../../hooks/useOmsFilterCriteria";
import { columnValueError, comparisonLabel } from "./omsReportFormatting";
import OMSFilterValue, { prefersOmsChoices } from "./OMSFilterValue";

function filterFields(
  report: OmsReport,
  state: OmsFilterCriteriaState
): AttributeEditorProps.FieldDefinition<OmsFilterCondition>[] {
  return [
    {
      label: "Field",
      control: (criterion, index) => (
        <Select
          ariaLabel={`Field ${index + 1}`}
          filteringType="auto"
          filteringPlaceholder="Search report fields"
          filteringAriaLabel="Search report fields"
          filteringClearAriaLabel="Clear field search"
          noMatch="No matching fields in this report."
          placeholder="Choose a field"
          selectedOption={
            criterion.column
              ? { value: criterion.column, label: criterion.column }
              : null
          }
          options={report.columns.map((column) => ({
            value: column,
            label: column,
            description: {
              string: "Text",
              number: "Number",
              date: "Date",
              datetime: "Date and time",
            }[report.columnTypes?.[column] || "string"],
          }))}
          onChange={({ detail }) => {
            const column = detail.selectedOption.value || "";
            const choices = prefersOmsChoices(report, column);
            state.update(criterion.id, {
              column,
              operator: (choices ? "in" : "eq") as OmsFilterOperator,
              value: choices ? undefined : "",
              values: choices ? [] : undefined,
            });
          }}
        />
      ),
    },
    {
      label: "Condition",
      control: (criterion, index) => {
        const type = report.columnTypes?.[criterion.column] || "string";
        const allowed =
          type === "string"
            ? ["eq", "ne", "contains", "notContains", "isNull", "isNotNull"]
            : ["eq", "ne", "gt", "gte", "lt", "lte", "isNull", "isNotNull"];
        if (
          type === "string" &&
          (prefersOmsChoices(report, criterion.column) ||
            ["in", "notIn"].includes(criterion.operator))
        )
          allowed.unshift("in", "notIn");
        return (
          <Select
            ariaLabel={`Condition ${index + 1}`}
            disabled={!criterion.column}
            selectedOption={{
              value: criterion.operator,
              label: comparisonLabel(criterion.operator, type),
            }}
            options={allowed.map((value) => ({
              value,
              label: comparisonLabel(value as OmsFilterOperator, type),
            }))}
            onChange={({ detail }) => {
              const operator = detail.selectedOption.value as OmsFilterOperator;
              const membership = ["in", "notIn"].includes(operator);
              state.update(criterion.id, {
                operator,
                value:
                  membership || ["isNull", "isNotNull"].includes(operator)
                    ? undefined
                    : "",
                values: membership ? [] : undefined,
              });
            }}
          />
        );
      },
    },
    {
      label: "Value",
      errorText: (criterion) =>
        criterion.column && (criterion.value || criterion.values?.length)
          ? columnValueError(
              report.columnTypes?.[criterion.column] || "string",
              criterion.operator,
              criterion.value,
              criterion.values
            )
          : undefined,
      constraintText: (criterion) => {
        if (
          !criterion.column ||
          ["isNull", "isNotNull"].includes(criterion.operator)
        )
          return undefined;
        const type = report.columnTypes?.[criterion.column] || "string";
        if (type === "date") return "Use YYYY-MM-DD.";
        if (type === "datetime") return "Use UTC or an offset such as +01:00.";
        if (["in", "notIn"].includes(criterion.operator)) return undefined;
        if (type === "string")
          return ["contains", "notContains"].includes(criterion.operator)
            ? "Text matching is case-sensitive."
            : "Case-sensitive. Leave blank for an empty value.";
        return undefined;
      },
      control: (criterion, index) => (
        <OMSFilterValue
          report={report}
          criterion={criterion}
          index={index}
          update={state.update}
        />
      ),
    },
  ];
}
export function AppliedOmsFilters({
  report,
  filters,
  operation,
  textCaseSensitive = false,
}: {
  report: OmsReport;
  filters: OmsRecordFilter[];
  operation: "and" | "or";
  textCaseSensitive?: boolean;
}) {
  const matchingLabel =
    operation === "or" ? "Any condition (OR)" : "All conditions (AND)";
  return (
    <Container
      header={
        <Header
          variant="h3"
          description={filters.length > 1 ? matchingLabel : undefined}
        >
          Applied filters
        </Header>
      }
    >
      <ul className="oms-report-download__applied-filters">
        {filters.map((filter, index) => (
          // Accepted conditions are immutable and may repeat.
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${index}:${filter.column}`}>
            <strong>{filter.column}</strong>{" "}
            {comparisonLabel(
              filter.operator,
              report.columnTypes?.[filter.column]
            ).toLowerCase()}
            {!textCaseSensitive &&
              ["contains", "notContains"].includes(filter.operator) &&
              " (case-insensitive)"}
            {filter.value !== undefined && (
              <>
                {" "}
                <span>{filter.value === "" ? '"" (blank)' : filter.value}</span>
              </>
            )}
            {filter.values && (
              <>
                {" "}
                <span>
                  {filter.values
                    .map((value) =>
                      value === "" ? '"" (blank)' : JSON.stringify(value)
                    )
                    .join(", ")}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </Container>
  );
}
export default function OMSRecordFilters({
  report,
  state,
}: {
  report: OmsReport;
  state: OmsFilterCriteriaState;
}) {
  return (
    <Container
      header={
        <Header
          variant="h3"
          description="Filter the scheduled report. Dashboard filters do not apply."
        >
          Report filters
        </Header>
      }
    >
      <SpaceBetween size="m">
        {state.criteria.length > 1 && (
          <div className="oms-report-download__matching">
            <FormField label="Include rows matching">
              <Select
                selectedOption={{
                  value: state.operation,
                  label:
                    state.operation === "and"
                      ? "All conditions (AND)"
                      : "Any condition (OR)",
                }}
                options={[
                  { value: "and", label: "All conditions (AND)" },
                  { value: "or", label: "Any condition (OR)" },
                ]}
                onChange={({ detail }) =>
                  state.setOperation(
                    detail.selectedOption.value as "and" | "or"
                  )
                }
              />
            </FormField>
          </div>
        )}
        <AttributeEditor
          items={state.criteria}
          definition={filterFields(report, state)}
          gridLayout={[{ rows: [[3, 3, 4]], removeButton: { width: "auto" } }]}
          addButtonText="Add filter"
          removeButtonText="Remove"
          removeButtonAriaLabel={(item) =>
            `Remove filter ${state.criteria.indexOf(item) + 1}`
          }
          isItemRemovable={() => state.criteria.length > 1}
          disableAddButton={state.criteria.length >= 20}
          additionalActions={
            <Button onClick={state.clear}>Clear all filters</Button>
          }
          additionalInfo={
            state.criteria.length >= 20
              ? "You can add up to 20 filters."
              : undefined
          }
          i18nStrings={{
            itemRemovedAriaLive: "Filter removed",
            errorIconAriaLabel: "Error",
          }}
          onAddButtonClick={state.add}
          onRemoveButtonClick={({ detail }) => state.remove(detail.itemIndex)}
        />
      </SpaceBetween>
    </Container>
  );
}
